import { Component, ViewChild, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { LoaderType, LoadingService } from '../../services/loading.service';
import { ToastrService } from 'ngx-toastr';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { TranslateService } from '@ngx-translate/core';
import validationEngine from 'devextreme/ui/validation_engine';
import { Observable, Subject, debounceTime, distinctUntilChanged, forkJoin, switchMap } from 'rxjs';
import { GoogleMapsService } from '../../services/google-maps.service';
import { DxDropDownBoxComponent } from 'devextreme-angular';
import { TrainingTypeModel } from "../../models/training/training-type.model";
import { TrainingCategoryModel } from 'src/app/models/training-category/training-category.model';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { ApprovalRequestExternalCertificateSummaryModel } from 'src/app/models/approval-request/approval-request-external-certificate-summary.model';
import { ApprovalRequestService } from 'src/app/services/approval-request.service';
import { ExternalCertificateReviewModel } from 'src/app/models/approval-request/external-certificate-review.model';
import { IDD_MIN_TRAINING_DURATION_MINUTES } from 'src/app/constants/idd.constants';

@Component({
  selector: 'app-review-external-certificate-dialog',
  standalone: false,
  templateUrl: './review-external-certificate-dialog.component.html',
  styleUrl: './review-external-certificate-dialog.component.scss'
})
export class ReviewExternalCertificateDialogComponent implements OnInit {
  @ViewChild('updateRequestForm', { static: true })
  updateRequestForm!: DxFormComponent;

  @ViewChild(DxDropDownBoxComponent, { static: false })
  trainingGroupDropDown!: DxDropDownBoxComponent;

  trainingCategoryDropDown!: DxDropDownBoxComponent;
  trainingTypeOptions!: DxSelectBoxTypes.Properties;

  @Input()
  editingPersonId!: string;

  @Input()
  externalRequest!: ApprovalRequestExternalCertificateSummaryModel;

  @Output()
  onRequestReviewed = new EventEmitter();

  form = new UntypedFormGroup({
    name: new UntypedFormControl(null, [Validators.required]),
    description: new UntypedFormControl(null, []),
    trainingType: new UntypedFormControl(null, [Validators.required]),
    iddHours: new UntypedFormControl(0, [Validators.required]),
    iddMinutes: new UntypedFormControl(0, [Validators.required]),
    date: new UntypedFormControl(null, [Validators.required]),
    location: new UntypedFormControl(null, []),
    trainingCategoryId: new UntypedFormControl(null, []),
    certificateFile: new UntypedFormControl(null, []),
  });

  reviewForm: ExternalCertificateReviewModel 
    = <ExternalCertificateReviewModel>{};

  trainingCategories: TrainingCategoryModel[] = [];
  googleSearchText: string = '';
  googlePlaces: any[] = [];

  showCertificateFilePopup = false;
  showDeclineRequestPopup = false;

  private googlePlaceinputChanged = new Subject<string>();
  googleAutocompleteResults$ = this.googlePlaceinputChanged.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    switchMap((searchTerm) => this.mapsService.getAutocomplete(searchTerm))
  );

  constructor(
    private trainingCategoryService: TrainingCategoryService,
    private toastrService: ToastrService,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private mapsService: GoogleMapsService,
    private approvalRequestService: ApprovalRequestService,
  ) {
    this.autocompleteSearchFilter = this.autocompleteSearchFilter.bind(this);
  }

  ngOnInit(): void {
    this.loadingService.load(
      this.trainingCategoryService.getAllTrainingCategories()
    ).subscribe((trainingCategories: any) => {
      this.trainingCategories = trainingCategories;
      this.setDropdownOptions();
  
      this.googleAutocompleteResults$.subscribe((results) => {
        this.googlePlaces = results;
      });
  
      this.reviewForm.name = this.externalRequest.eventName;
      this.reviewForm.description = this.externalRequest.eventDescription;
      this.reviewForm.trainingType = this.externalRequest.trainingType;
      this.reviewForm.iddTimeMinutes = this.externalRequest.iddTimeMinutes;
      this.reviewForm.date = new Date(this.externalRequest.eventDate);
      this.reviewForm.trainingCategoryId =
        this.externalRequest.trainingCategory
          ? this.externalRequest.trainingCategory.id
          : null;
      this.reviewForm.location = this.externalRequest.location;
      this.reviewForm.iddHours = this.reviewForm.iddTimeMinutes
        ? Math.floor(this.reviewForm.iddTimeMinutes / 60)
        : 0;
      this.reviewForm.iddMinutes = this.reviewForm.iddTimeMinutes
        ? this.reviewForm.iddTimeMinutes % 60
        : 0;
      this.googleSearchText = this.externalRequest.location || '';
    });
  }

  setDropdownOptions() {
    const defaultSelectOptions = {
      showClearButton: true,
      stylingMode: 'outlined',
      displayExpr: 'name',
      valueExpr: 'id',
    } as DxSelectBoxTypes.Properties;

    this.trainingTypeOptions = {
      ...defaultSelectOptions,
      dataSource: [
        {
          id: 0,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.IDD'
          ),
        },
        {
          id: 1,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
          ),
        },
        {
          id: 2,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
          ),
        },
      ],
      placeholder: '',
    };
  }

  onTrainingCategorySelected(e: any) {
    if (e.node.key) {
      this.reviewForm.trainingCategoryId = e.node.key;
    }
  }

  onFormSubmit() {
    const isFormValid = this.isFormValid();
    if (isFormValid) {
      const reviewModel: ExternalCertificateReviewModel = this.prepareRequestModel();

      this.loadingService
        .load(
          this.approvalRequestService.reviewExternalCertificate(
            this.externalRequest.id,
            reviewModel
          )
        )
        .subscribe({
          next: () => {
            this.toastrService.success(
              this.translateService.instant(
                'APPROVAL-REQUEST.APPROVAL-REQUEST-UPDATED'
              )
            );
            this.onRequestReviewed.emit();
          },
          error: () => {
            this.toastrService.error(
              this.translateService.instant(
                'COMMON.AN-ERROR-HAS-OCCURRED'
              )
            );
          },
        });
    }
  }

  handleTimePicker(value: { hours: number; minutes: number }) {
    this.reviewForm.iddHours = value.hours;
    this.reviewForm.iddMinutes = value.minutes;
  }

  autocompleteSearchFilter(e: any) {
    return this.googleSearchText;
  }

  getGooglePlaces(e: any) {
    this.googlePlaceinputChanged.next(e.value);
  }

  setLocationPlace(e: any) {
    this.loadingService
      .load(this.mapsService.getDetails(e.itemData.place_id))
      .subscribe((details) => {
        const location = details.formatted_address.startsWith(details.name)
          ? details.formatted_address
          : details.name + ', ' + details.formatted_address;
        this.reviewForm.location = location;
      });
  }

  isTimeValid(): boolean {
    const isHoursValid = (
      this.reviewForm.iddHours && 
      this.reviewForm.iddHours > 0
    );

    const isMinutesValid = (
      this.reviewForm.iddMinutes && 
      this.reviewForm.iddMinutes >= IDD_MIN_TRAINING_DURATION_MINUTES
    );

    return !!(isHoursValid || isMinutesValid);
  }

  isFormValid(): boolean {
    const isCertificateFormValid =
      this.updateRequestForm.instance.validate().isValid;
    const isCustomFieldsValid =
      validationEngine.validateGroup('customFields').isValid;

    let isFormValid: boolean = !!(isCertificateFormValid && isCustomFieldsValid);

    if(this.reviewForm.trainingType === TrainingTypeModel.IDD && !this.isTimeValid()) {
      return false;
    }
    
    return isFormValid;
  }
 
  prepareRequestModel() {
    const date: Date = this.reviewForm.date!;
    date.setHours(12);

    if (this.reviewForm.trainingType !== 0) {
      this.reviewForm.iddTimeMinutes = 0;
    } else {
      this.reviewForm.iddTimeMinutes =
        this.reviewForm.iddHours! * 60 +
        this.reviewForm.iddMinutes!;
    }

    const requestModel: ExternalCertificateReviewModel = {
      ...this.reviewForm,
      date,
    };

    if (!requestModel.trainingCategoryId) {
      requestModel.trainingCategoryId = null;
    }

    // delete requestModel.iddHours;
    // delete requestModel.iddMinutes;

    return requestModel;
  }

  openCertificateFilePopup(): void {
    this.showCertificateFilePopup = true;
  }

  closeCertificateFilePopup(): void {
    this.showCertificateFilePopup = false;
  }

  openDeclineRequestPopup() {
    this.showDeclineRequestPopup = true;
  }

  onSubmitDeclineRequestPopup() {
    this.showDeclineRequestPopup = true;
    this.onRequestReviewed.emit();
  }
}
